/**
 * Finanzas → Movimientos de cuenta (`/finanzas/movimientos`).
 *
 * Portado de `Bank_accounts::movements` + `views/bank_accounts/movements.php` y de
 * su bundle `src/bank_accounts/movements.js`, que pedía los datos a la API interna
 * `API/Accounts`.
 *
 * La pantalla es el listado de los movimientos de las cuentas bancarias del rango
 * elegido, en dos pestañas (**activos** y **anulados**), con los totales de
 * ingresos, egresos y su diferencia arriba —que es lo primero que mira el usuario—
 * y el detalle de cada fila en un diálogo, donde también se **anula**.
 *
 * Reglas heredadas:
 * - El rango por defecto es el **mes en curso** (del día 1 a hoy), como el
 *   original.
 * - El filtro de cuenta sólo ofrece las cuentas del banco elegido.
 * - El signo y el color del monto salen de la **dirección** del tipo de
 *   movimiento (INGRESO suma, EGRESO resta), y los totales se calculan sobre todo
 *   el rango filtrado, no sobre la página.
 * - Anular sólo se ofrece a los tipos que el original permitía y a los roles de
 *   gestión; el botón desaparece cuando no aplica.
 */

import {
  ArrowDownLeft,
  ArrowUpRight,
  Ban,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileSpreadsheet,
  FileText,
  Info,
  RefreshCw,
  Scale,
  Search,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, toIsoDate } from '@/lib/utils';

import { MovimientoDetalleDialog } from '../components/movimiento-detalle-dialog';
import {
  movimientosApi,
  useAnularMovimiento,
  useCatalogosMovimientos,
  useMovimientos,
} from '../movimientos.api';
import type {
  EstadoMovimientos,
  MovimientoCuenta,
  MovimientosFiltros,
} from '../movimientos.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const TAMANOS = [10, 25, 50, 100];

/** Tonos del distintivo del tipo, según el color que trae el catálogo. */
const TONOS: Record<string, 'success' | 'danger' | 'warning' | 'brand' | 'neutral'> = {
  success: 'success',
  danger: 'danger',
  warning: 'warning',
  info: 'brand',
  primary: 'brand',
  secondary: 'neutral',
};

/** Primer día del mes en curso. */
function inicioDeMes(): string {
  const hoy = new Date();

  return toIsoDate(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
}

/** Color del estado del control de efectivo, para la fecha de anulación. */
function tonoDe(color: string | null, direccion: string) {
  if (color && TONOS[color]) return TONOS[color];

  return direccion === 'EGRESO' ? 'danger' : 'success';
}

export function MovimientosPage() {
  useDocumentTitle('Finanzas · Movimientos de cuenta');

  const [desde, setDesde] = useState(inicioDeMes);
  const [hasta, setHasta] = useState(() => toIsoDate(new Date()));
  const [banco, setBanco] = useState('');
  const [cuenta, setCuenta] = useState('');
  const [pestana, setPestana] = useState<EstadoMovimientos>('activos');
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(10);
  const [detalle, setDetalle] = useState<MovimientoCuenta | null>(null);
  const [porAnular, setPorAnular] = useState<MovimientoCuenta | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filtros = useMemo<MovimientosFiltros>(
    () => ({
      desde,
      hasta,
      banco: banco ? Number(banco) : 0,
      cuenta: cuenta ? Number(cuenta) : 0,
      estado: pestana,
      search: busqueda.trim(),
      page: pagina,
      perPage: limite,
    }),
    [desde, hasta, banco, cuenta, pestana, busqueda, pagina, limite],
  );

  const consulta = useMovimientos(filtros);
  const catalogos = useCatalogosMovimientos();
  const anular = useAnularMovimiento();

  const movimientos = consulta.data?.movimientos ?? [];
  const totales = consulta.data?.totales;
  const total = consulta.data?.total ?? 0;
  const paginas = consulta.data?.paginas ?? 1;

  /** Las cuentas del banco elegido (el original filtraba igual). */
  const cuentasDelBanco = (catalogos.data?.cuentas ?? []).filter(
    (item) => !banco || item.banco_id === Number(banco),
  );

  /** Cambiar cualquier filtro vuelve a la primera página. */
  const filtrar = (cambio: () => void) => {
    setPagina(1);
    cambio();
  };

  const exportar = async (formato: 'csv' | 'pdf') => {
    setError(null);

    try {
      await abrirPdfAutenticado(movimientosApi.urlExportar(filtros, formato), {
        nombre:
          formato === 'csv'
            ? `movimientos-cuenta-${desde}_${hasta}.csv`
            : `movimientos-cuenta-${desde}_${hasta}.pdf`,
      });
    } catch (fallo) {
      setError(
        fallo instanceof Error ? fallo.message : 'No se pudo generar la exportación.',
      );
    }
  };

  const confirmarAnulacion = () => {
    if (!porAnular) return;

    setError(null);

    anular.mutate(porAnular.id, {
      onSuccess: () => {
        setAviso(`Movimiento #${porAnular.id} anulado.`);
        setPorAnular(null);
        setDetalle(null);
        setTimeout(() => setAviso(null), 6000);
      },
      onError: (fallo) => {
        setError(fallo instanceof Error ? fallo.message : 'No se pudo anular el movimiento.');
        setPorAnular(null);
      },
    });
  };

  const diferencia = totales?.diferencia ?? 0;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Movimientos de cuenta</h1>
          <p className="mt-1 text-sm text-ink-500">
            Los cargos y abonos de las cuentas bancarias: lo que entró por cada vía de cobro y lo
            que salió por pagos, depósitos y gastos.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={<FileSpreadsheet className="size-4" aria-hidden="true" />}
            onClick={() => void exportar('csv')}
          >
            Excel
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
            onClick={() => void exportar('pdf')}
          >
            PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="error" title="No se pudo completar la acción">
          {error}
        </Alert>
      ) : null}

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Desde
              <CampoFecha
                tipo="date"
                value={desde}
                max={hasta}
                onChange={(evento) => filtrar(() => setDesde(evento.target.value))}
                aria-label="Fecha de inicio"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Hasta
              <CampoFecha
                tipo="date"
                value={hasta}
                min={desde}
                onChange={(evento) => filtrar(() => setHasta(evento.target.value))}
                aria-label="Fecha de fin"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Banco
              <Select
                value={banco}
                onChange={(evento) =>
                  filtrar(() => {
                    setBanco(evento.target.value);
                    setCuenta('');
                  })
                }
                className={CONTROL}
                aria-label="Banco"
              >
                <option value="">Todos los bancos</option>
                {(catalogos.data?.bancos ?? []).map((item) => (
                  <option key={item.id} value={String(item.id)}>
                    {item.descripcion}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Cuenta
              <Select
                value={cuenta}
                onChange={(evento) => filtrar(() => setCuenta(evento.target.value))}
                className={CONTROL}
                aria-label="Cuenta"
              >
                <option value="">Todas las cuentas</option>
                {cuentasDelBanco.map((item) => (
                  <option key={item.id} value={String(item.id)}>
                    {item.descripcion}
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-xs font-medium text-ink-500">
              Buscar
              <span className="relative block">
                <Search
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                  aria-hidden="true"
                />
                <Input
                  value={busqueda}
                  onChange={(evento) => filtrar(() => setBusqueda(evento.target.value))}
                  placeholder="Cliente, referencia, documento, monto…"
                  className="pl-9"
                  aria-label="Buscar movimiento"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Filas
              <Select
                value={String(limite)}
                onChange={(evento) => filtrar(() => setLimite(Number(evento.target.value)))}
                className={cn(CONTROL, 'w-24')}
                aria-label="Filas por página"
              >
                {TAMANOS.map((tamano) => (
                  <option key={tamano} value={String(tamano)}>
                    {tamano}
                  </option>
                ))}
              </Select>
            </label>

            <span className="ml-auto text-xs text-ink-500">
              {total.toLocaleString('es-PE')} movimiento{total === 1 ? '' : 's'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Totales del rango */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"
            >
              <ArrowUpRight className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Total ingresos
              </p>
              <p className="text-base font-bold break-words text-emerald-700 tabular-nums sm:text-lg">
                {formatCurrency(totales?.ingresos ?? 0)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-600"
            >
              <ArrowDownLeft className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Total egresos
              </p>
              <p className="text-base font-bold break-words text-rose-700 tabular-nums sm:text-lg">
                {formatCurrency(totales?.egresos ?? 0)}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card
          className={cn(
            'sm:col-span-2 xl:col-span-1',
            diferencia > 0
              ? 'border-emerald-200 bg-emerald-50/60'
              : diferencia < 0
                ? 'border-rose-200 bg-rose-50/60'
                : 'border-amber-200 bg-amber-50/60',
          )}
        >
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className={cn(
                'flex size-10 shrink-0 items-center justify-center rounded-xl',
                diferencia > 0
                  ? 'bg-emerald-100 text-emerald-700'
                  : diferencia < 0
                    ? 'bg-rose-100 text-rose-700'
                    : 'bg-amber-100 text-amber-700',
              )}
            >
              <Scale className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Diferencia (ingresos − egresos)
              </p>
              <p
                className={cn(
                  'text-base font-bold break-words tabular-nums sm:text-lg',
                  diferencia > 0
                    ? 'text-emerald-700'
                    : diferencia < 0
                      ? 'text-rose-700'
                      : 'text-amber-700',
                )}
              >
                {formatCurrency(diferencia)}
              </p>
              <p className="text-xs text-ink-500">
                {totales?.estado ?? 'Equilibrado'}
                {totales ? ` · ${totales.movimientos} movimiento(s)` : ''}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Listado */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            {(
              [
                { valor: 'activos' as const, etiqueta: 'Movimientos activos' },
                { valor: 'anulados' as const, etiqueta: 'Movimientos anulados' },
              ]
            ).map((opcion) => (
              <button
                key={opcion.valor}
                type="button"
                aria-pressed={pestana === opcion.valor}
                onClick={() => filtrar(() => setPestana(opcion.valor))}
                className={cn(
                  'inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                  pestana === opcion.valor
                    ? 'border-brand-300 bg-brand-50 text-brand-700'
                    : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                )}
              >
                {opcion.valor === 'anulados' ? (
                  <Ban className="size-3.5" aria-hidden="true" />
                ) : null}
                {opcion.etiqueta}
                {pestana === opcion.valor ? ` · ${total}` : ''}
              </button>
            ))}
          </div>

          {consulta.isError ? (
            <Alert variant="error" title="No se pudieron leer los movimientos">
              {consulta.error instanceof ApiError
                ? consulta.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
            </Alert>
          ) : null}

          <div className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2 whitespace-nowrap">
                    {pestana === 'anulados' ? 'Anulación' : 'Registro'}
                  </th>
                  <th className="px-2 py-2 whitespace-nowrap">Movimiento</th>
                  <th className="px-2 py-2">Cliente / Proveedor</th>
                  <th className="px-2 py-2">Referencia</th>
                  <th className="px-2 py-2">Banco / Cuenta</th>
                  <th className="px-2 py-2">Tipo</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Monto</th>
                  <th className="px-2 py-2 text-center whitespace-nowrap">Comprobante</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {consulta.isLoading
                  ? Array.from({ length: 5 }).map((_, fila) => (
                      <tr key={fila} className="border-t border-ink-100">
                        {Array.from({ length: 9 }).map((__, columna) => (
                          <td key={columna} className="px-2 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : movimientos.map((fila) => {
                      const tono = tonoDe(fila.tipo_color, fila.direccion);

                      return (
                        <tr
                          key={fila.id}
                          className={cn(
                            'border-t border-ink-100',
                            pestana === 'anulados' && 'text-ink-500',
                          )}
                        >
                          <td className="px-2 py-2 whitespace-nowrap">
                            <p className="font-semibold text-ink-800">
                              {pestana === 'anulados'
                                ? fila.anulado_el ?? '—'
                                : fila.registrado ?? '—'}
                            </p>
                            {pestana === 'anulados' && fila.anulado_por ? (
                              <p className="text-[11px] text-ink-500">
                                por {fila.anulado_por}
                              </p>
                            ) : null}
                          </td>

                          <td className="px-2 py-2 whitespace-nowrap">
                            {formatDateValue(fila.fecha)}
                          </td>

                          <td className="max-w-[16rem] px-2 py-2">
                            <p className="truncate" title={fila.cliente ?? ''}>
                              {fila.cliente ?? 'N/A'}
                            </p>
                            {fila.documento ? (
                              <p className="truncate text-[11px] text-ink-500">
                                {fila.documento}
                              </p>
                            ) : null}
                          </td>

                          <td className="max-w-[12rem] px-2 py-2">
                            <span className="block truncate" title={fila.referencia ?? ''}>
                              {fila.referencia || '—'}
                            </span>
                          </td>

                          <td className="px-2 py-2">
                            <p className="font-medium text-ink-800">{fila.banco.trim()}</p>
                            <p className="text-[11px] text-ink-500">{fila.cuenta}</p>
                          </td>

                          <td className="px-2 py-2">
                            <Badge variant={tono}>{fila.tipo}</Badge>
                            {fila.subtipo ? (
                              <p className="mt-0.5 text-[11px] text-ink-500">{fila.subtipo}</p>
                            ) : null}
                          </td>

                          <td
                            className={cn(
                              'px-2 py-2 text-right font-semibold whitespace-nowrap tabular-nums',
                              fila.direccion === 'EGRESO' ? 'text-rose-600' : 'text-emerald-700',
                            )}
                          >
                            {fila.direccion === 'EGRESO' ? '−' : '+'}{' '}
                            {formatCurrency(fila.monto_absoluto)}
                          </td>

                          <td className="px-2 py-2 text-center">
                            {fila.comprobante ? (
                              <span
                                className="text-ink-500"
                                title={fila.comprobante.archivo}
                              >
                                <FileText className="mx-auto size-4" aria-hidden="true" />
                              </span>
                            ) : (
                              <span className="text-ink-300">—</span>
                            )}
                          </td>

                          <td className="px-2 py-2 text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Ver el detalle del movimiento"
                                aria-label={`Ver el detalle del movimiento ${fila.id}`}
                                onClick={() => setDetalle(fila)}
                              >
                                <Eye className="size-4" aria-hidden="true" />
                              </Button>

                              {fila.puede_anular ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  title="Anular el movimiento"
                                  aria-label={`Anular el movimiento ${fila.id}`}
                                  onClick={() => setPorAnular(fila)}
                                >
                                  <Ban className="size-4 text-rose-600" aria-hidden="true" />
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-ink-500">
              Página {pagina} de {paginas.toLocaleString('es-PE')}
              {total > 0
                ? ` · mostrando ${((pagina - 1) * limite + 1).toLocaleString('es-PE')}–${Math.min(
                    pagina * limite,
                    total,
                  ).toLocaleString('es-PE')}`
                : ''}
            </span>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagina <= 1}
                leftIcon={<ChevronLeft className="size-4" aria-hidden="true" />}
                onClick={() => setPagina((actual) => Math.max(1, actual - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagina >= paginas}
                rightIcon={<ChevronRight className="size-4" aria-hidden="true" />}
                onClick={() => setPagina((actual) => actual + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        Los movimientos se registran desde las demás pantallas —cobros de visa, transferencias,
        pagos de créditos, compras, depósitos de efectivo, ingresos y egresos administrativos— y
        aquí sólo se consultan y se anulan. Al anular un pago de crédito se devuelve lo aplicado a
        sus facturas, y al anular un depósito de efectivo se recalcula su control.
      </p>

      <MovimientoDetalleDialog
        movimiento={detalle}
        onClose={() => setDetalle(null)}
        onAnular={(fila) => setPorAnular(fila)}
      />

      <ConfirmDialog
        open={Boolean(porAnular)}
        title="Anular el movimiento"
        description={
          porAnular
            ? `Se anulará el movimiento #${porAnular.id} (${porAnular.tipo} por ${formatCurrency(
                porAnular.monto_absoluto,
              )}).${
                porAnular.tipo_codigo === 'PAG_CRE'
                  ? ' Se devolverá el monto aplicado a sus facturas.'
                  : porAnular.tipo_codigo === 'DEP_EFE'
                    ? ' Se borrará el depósito y se recalculará el control de efectivo.'
                    : ''
              } Esta acción no se puede deshacer.`
            : ''
        }
        confirmLabel="Anular movimiento"
        loading={anular.isPending}
        onConfirm={confirmarAnulacion}
        onClose={() => setPorAnular(null)}
      />
    </div>
  );
}
